import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { getDecagonChatPath } from 'helpers/link-generation';
import { copyToClipboard } from 'helpers/page-utils';

import './ItemSubmitErrorModal.scss';

const { block, element } = bem('item-submit-error-modal');

const messages = defineMessages({
  title: {
    id: 'items.item-submit-error-modal.title',
    defaultMessage: 'Uh oh! We were unable to submit your item',
  },
  description: {
    id: 'items.item-submit-error-modal.description',
    defaultMessage:
      'This can happen due to temporary technical difficulties. Please contact us with the following info to ensure your item is submitted:',
  },
  addPricing: {
    id: 'items.item-submit-error-modal.add-pricing',
    defaultMessage: 'Hi, I’d like to add pricing for my menu item:',
  },
  itemName: {
    id: 'items.item-submit-error-modal.item-name',
    defaultMessage: 'Item name: <strong>{itemName}</strong>',
  },
  menuPrice: {
    id: 'items.item-submit-error-modal.menu-price',
    defaultMessage: 'Menu price: <strong>{menuPrice}</strong>',
  },
  menuLink: {
    id: 'items.item-submit-error-modal.menu-link',
    defaultMessage: 'Menu link: <strong>{linkToMenu}</strong>',
  },
  copy: {
    id: 'items.item-submit-error-modal.copy',
    defaultMessage: 'Copy text',
  },
  contactUs: {
    id: 'items.item-submit-error-modal.contact-us',
    defaultMessage: 'Contact us',
  },
  addPricingTemplate: {
    id: 'items.item-submit-error-modal.add-pricing-template',
    defaultMessage: `Hi, I’d like to add pricing for my menu item:
    Item name: {itemName}
    Menu price: {menuPrice}
    Menu link: {linkToMenu}`,
  },
});

export const ITEM_SUBMIT_ERROR_MODAL_NAME = 'ItemSubmitErrorModal';

function ItemSubmitErrorModal({ currencyCode, itemName, linkToMenu, menuPrice }) {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const decagonChatPath = getDecagonChatPath(pathname, search);
  const intl = useIntl();
  const formattedMenuPrice = intl.formatNumber(menuPrice, { style: 'currency', currency: currencyCode });

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleCopyToClipboard = () => {
    const copyText = intl.formatMessage(messages.addPricingTemplate, {
      itemName,
      menuPrice: formattedMenuPrice,
      linkToMenu,
    });
    copyToClipboard(copyText);
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <Isvg src={ILLUSTRATIONS.errorTools} className={element('illustration')} />
        <Isvg src={ICONS.cross} className={element('close-icon')} onClick={handleClose} />
      </header>
      <section className={element('content')}>
        <div className={element('content-wrapper')}>
          <Message className={element('text-center', 'text--md', 'text--semibold')} message={messages.title} />
          <Message className={element('text-center')} message={messages.description} />
          <Button
            className={element('copy-btn')}
            onClick={handleCopyToClipboard}
            styled="tertiary"
            type={Button.TYPES.BUTTON}>
            <Isvg src={ICONS.copy2} className={element('copy-icon')} />
            <Message message={messages.copy} />
          </Button>
          <div className={element('pricing-template-container')}>
            <Message message={messages.addPricing} />
            <div className={element('pricing-template')}>
              <Message message={messages.itemName} values={{ itemName, strong: text => <strong>{text}</strong> }} />
              <Message
                message={messages.menuPrice}
                values={{ menuPrice: formattedMenuPrice, strong: text => <strong>{text}</strong> }}
              />
              <Message message={messages.menuLink} values={{ linkToMenu, strong: text => <strong>{text}</strong> }} />
            </div>
          </div>
        </div>
        <Link className={element('contact-link')} to={decagonChatPath}>
          <Message
            as={Button}
            className={element('contact-btn')}
            message={messages.contactUs}
            styled="primary"
            type={Button.TYPES.BUTTON}
          />
        </Link>
      </section>
    </div>
  );
}

ItemSubmitErrorModal.propTypes = {
  currencyCode: PropTypes.string,
  itemName: PropTypes.string,
  linkToMenu: PropTypes.string,
  menuPrice: PropTypes.string,
};

export default ItemSubmitErrorModal;
